import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import axios from 'axios';
import { Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    inquiry_type: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // { type: 'success' | 'error', message: string }

  // Listen for inquiry type pre-selection from other components
  useEffect(() => {
    const handler = (e) => {
      setFormData(prev => ({ ...prev, inquiry_type: e.detail }));
    };
    window.addEventListener('preselectInquiry', handler);
    return () => window.removeEventListener('preselectInquiry', handler);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Skip GSAP on mobile — show content immediately
    if (isMobile) {
      if (contentRef.current) {
        Array.from(contentRef.current.children).forEach(child => {
          child.style.opacity = '1';
          child.style.transform = 'none';
        });
      }
      return;
    }

    gsap.fromTo(
      contentRef.current?.children || [],
      { y: 60, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        stagger: 0.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: contentRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }, [isMobile]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: response.data.message
        });
        // Clear form on success
        setFormData({
          name: '',
          email: '',
          company: '',
          message: '',
          inquiry_type: 'general'
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: response.data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to send message. Please try again or email us directly.'
      });
    } finally {
      setIsSubmitting(false);
      
      // Clear status message after 10 seconds
      setTimeout(() => setSubmitStatus(null), 10000);
    }
  };

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'omniagency', label: 'OmniAgency' },
    { value: 'verified', label: 'Verified' },
    { value: 'thedossier', label: 'The Dossier' },
    { value: 'omnidraftlab', label: 'OmniDraftLab' },
    { value: 'omniathletics', label: 'OmniAthletics' },
    { value: 'omniscouting', label: 'OmniScouting' },
    { value: 'partnership', label: 'Partnership Opportunity' },
    { value: 'custom', label: 'Custom Project / Consulting' }
  ];

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="section relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0A0A0A 0%, #0F0F0F 100%)' }}
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#D3FF62]/15 rounded-full blur-[150px] pointer-events-none" />
      </div>

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Content */}
          <div ref={contentRef}>
            {/* Tagline */}
            <motion.div
              whileInView={{ scale: [0.8, 1] }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D3FF62]/30 bg-[#D3FF62]/10 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-[#D3FF62] animate-pulse" />
              <span className="text-[#D3FF62] text-sm font-medium">Get In Touch</span>
            </motion.div>

            {/* Headline */}
            <h2 className="display-large text-white mb-6">
              LET'S BUILD THE<br />
              <span className="gradient-text">FUTURE TOGETHER</span>
            </h2>

            {/* Description */}
            <p className="body-large text-white/70 mb-4 max-w-lg">
              Whether you're a professional team, college program, high school athletic department, 
              agency, or athlete—we'd love to hear from you.
            </p>

            <p className="text-white/50 mb-8 max-w-lg">
              Tell us about your organization and what you're looking for. From athlete management 
              and NIL deal flow to custom sports technology solutions, our team is here to help.
            </p>

            {/* Email Contact */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 mb-8">
              <div className="w-12 h-12 rounded-full bg-[#D3FF62]/20 flex items-center justify-center">
                <Mail className="text-[#D3FF62]" size={24} />
              </div>
              <div>
                <p className="text-white/60 text-sm">Email us directly</p>
                <a 
                  href="mailto:info@omnisportsgroup.us" 
                  className="text-white font-semibold hover:text-[#D3FF62] transition-colors"
                >
                  info@omnisportsgroup.us
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="p-8 md:p-10 rounded-3xl bg-[#141414] border border-white/10">
              {submitStatus?.type === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#D3FF62]/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-[#D3FF62]" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Message Sent!</h3>
                  <p className="text-white/70 mb-6">
                    {submitStatus.message}
                  </p>
                  <button
                    onClick={() => setSubmitStatus(null)}
                    className="text-[#D3FF62] font-medium hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" aria-label="Contact form">
                  {/* Inquiry Type */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      What can we help you with? *
                    </label>
                    <select
                      name="inquiry_type"
                      value={formData.inquiry_type}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#D3FF62]/50 focus:ring-1 focus:ring-[#D3FF62]/50 transition-all appearance-none cursor-pointer"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23D3FF62'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                    >
                      {inquiryTypes.map((type) => (
                        <option key={type.value} value={type.value} className="bg-[#141414]">
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#D3FF62]/50 focus:ring-1 focus:ring-[#D3FF62]/50 transition-all"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#D3FF62]/50 focus:ring-1 focus:ring-[#D3FF62]/50 transition-all"
                      placeholder="you@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Company / Organization
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#D3FF62]/50 focus:ring-1 focus:ring-[#D3FF62]/50 transition-all"
                      placeholder="Your company or organization"
                    />
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#D3FF62]/50 focus:ring-1 focus:ring-[#D3FF62]/50 transition-all resize-none"
                      placeholder="Tell us about your needs..."
                    />
                  </div>

                  {/* Error Message */}
                  {submitStatus?.type === 'error' && (
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                      <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
                      <p className="text-red-400 text-sm">{submitStatus.message}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary justify-center text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#004534]/30 border-t-[#004534] rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send size={20} />
                      </>
                    )}
                  </button>

                  <p className="text-white/40 text-xs text-center">
                    By submitting this form, you agree to our privacy policy.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
